import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bike, Check, ChefHat, CircleCheck, Clock, Home, Package, Phone, Receipt, XCircle } from 'lucide-react';
import type { FulfillmentType, LocationPayload, OrderStatus } from '@pizza-roma/shared';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/hooks';
import { money, time, addressLine, initials } from '../../lib/format';
import { MapView, type MapMarker } from '../../lib/map';
import { useConnected, useRealtime } from '../../lib/realtime';
import { ErrorBox, PageLoading, StatusBadge } from '../../components/ui';
import { useI18n } from '../../i18n';
import type { Messages } from '../../i18n/en';

interface PublicOrder {
  id: string;
  number: number;
  status: OrderStatus;
  fulfillmentType: FulfillmentType;
  items: { name: string; quantity: number; lineTotalCents: number; options: { optionName: string }[] }[];
  subtotalCents: number;
  taxCents: number;
  deliveryFeeCents: number;
  tipCents: number;
  totalCents: number;
  address: { lat?: number; lng?: number; line1?: string; city?: string } | null;
  createdAt: string;
  terminal: boolean;
  driver: { name: string } | null;
  driverLocation: { lat: number; lng: number; headingDeg: number | null; capturedAt: string; etaMinutes?: number } | null;
  events: { type: string; at: string }[];
  restaurant: { name: string; slug: string; phone: string; location: { lat: number; lng: number }; prepTimeMinutes: number };
}

interface Step {
  key: string;
  label: string;
  desc: string;
  icon: JSX.Element;
  reached: OrderStatus[];
}

function deliverySteps(t: Messages): Step[] {
  const s = t.tracking.steps;
  return [
    { key: 'PAID', ...s.PAID, icon: <Receipt size={17} />, reached: ['PAID', 'ACCEPTED', 'PREPARING', 'READY', 'ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED'] },
    { key: 'PREPARING', ...s.PREPARING, icon: <ChefHat size={17} />, reached: ['PREPARING', 'READY', 'ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED'] },
    { key: 'READY', ...s.READY, icon: <Package size={17} />, reached: ['READY', 'ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED'] },
    { key: 'ASSIGNED', ...s.ASSIGNED, icon: <Bike size={17} />, reached: ['ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED'] },
    { key: 'OUT_FOR_DELIVERY', ...s.OUT_FOR_DELIVERY, icon: <Bike size={17} />, reached: ['OUT_FOR_DELIVERY', 'DELIVERED'] },
    { key: 'DELIVERED', ...s.DELIVERED, icon: <Home size={17} />, reached: ['DELIVERED'] },
  ];
}

function pickupSteps(t: Messages): Step[] {
  const d = deliverySteps(t);
  return [
    d[0],
    d[1],
    { ...d[2], desc: t.tracking.steps.READY_PICKUP },
    { key: 'PICKED_UP', ...t.tracking.steps.PICKED_UP, icon: <CircleCheck size={17} />, reached: ['PICKED_UP'] },
  ];
}

export function TrackingPage() {
  const { token = '' } = useParams();
  const { t, demo } = useI18n();
  const { data: o, error, loading, reload, setData } = useAsync(() => api.get<PublicOrder>(`/api/public/orders/${token}`), [token]);
  const [eta, setEta] = useState<number | null>(null);
  const connected = useConnected();

  useRealtime(
    o && !o.terminal ? { event: 'subscribe:order', payload: { publicToken: token } } : null,
    {
      'order.status.changed': () => void reload(),
      'tracking.ended': () => void reload(),
      'driver.location.updated': (p: LocationPayload) => {
        setData((cur) => (cur ? { ...cur, driverLocation: { lat: p.lat, lng: p.lng, headingDeg: p.headingDeg, capturedAt: p.capturedAt, etaMinutes: p.etaMinutes } } : cur));
        if (p.etaMinutes != null) setEta(p.etaMinutes);
      },
    },
    [o?.terminal],
  );

  const markers = useMemo<MapMarker[]>(() => {
    if (!o) return [];
    const m: MapMarker[] = [{ id: 'restaurant', kind: 'restaurant', ...o.restaurant.location, label: o.restaurant.name }];
    if (o.address?.lat != null && o.address.lng != null) m.push({ id: 'home', kind: 'home', lat: o.address.lat, lng: o.address.lng, label: t.tracking.yourPlace });
    if (o.driverLocation && !o.terminal) m.push({ id: 'driver', kind: 'driver', lat: o.driverLocation.lat, lng: o.driverLocation.lng, label: o.driver?.name });
    return m;
  }, [o, t]);

  if (loading && !o) return <PageLoading />;
  if (error || !o)
    return (
      <main className="pub-main">
        <ErrorBox error={error ?? new Error(t.tracking.notFound)} />
      </main>
    );

  const delivery = o.fulfillmentType === 'DELIVERY';
  const steps = delivery ? deliverySteps(t) : pickupSteps(t);
  const at = (type: string) => o.events.find((e) => e.type === type)?.at;
  const currentEta = o.status === 'OUT_FOR_DELIVERY' ? (eta ?? o.driverLocation?.etaMinutes ?? null) : null;
  const cancelled = o.status === 'CANCELLED' || o.status === 'REFUNDED';

  return (
    <main className="pub-main">
      <div className="row-between wrap" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="section-title" style={{ color: 'var(--red)' }}>
            {t.common.order(o.number)}
          </h1>
          <p className="muted small">{t.tracking.placedAt(time(o.createdAt), o.restaurant.name)}</p>
        </div>
        <div className="row">
          <StatusBadge status={o.status} />
          <span className="pill" style={{ background: '#fff', border: '1px solid var(--line)' }} title={connected ? t.tracking.liveUpdates : t.common.reconnecting}>
            <span className={`live-dot ${connected && !o.terminal ? '' : 'off'}`} /> {o.terminal ? t.tracking.finished : connected ? t.tracking.live : t.tracking.reconnect}
          </span>
        </div>
      </div>

      <div className="track">
        <div className="stack-lg">
          {cancelled ? (
            <div className="alert alert-error row">
              <XCircle size={18} /> {t.tracking.cancelledMsg(o.status === 'REFUNDED', o.restaurant.phone)}
            </div>
          ) : (
            o.driver &&
            delivery && (
              <div className="driver-card" data-testid="driver-card">
                <div className="avatar">{initials(o.driver.name)}</div>
                <div className="grow">
                  <b style={{ color: 'var(--green-dark)', fontSize: 18 }}>{o.status === 'DELIVERED' ? t.tracking.deliveredBy(o.driver.name) : t.tracking.onTheWay(o.driver.name)}</b>
                  {currentEta != null && (
                    <div>
                      {t.tracking.etaIn}{' '}
                      <b style={{ color: 'var(--red)' }} data-testid="eta">
                        {currentEta} min
                      </b>
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          <div className="card card-pad timeline" data-testid="timeline">
            {steps.map((s) => {
              const done = s.reached.includes(o.status);
              const idx = steps.findIndex((x) => x.reached.includes(o.status) && !steps[steps.indexOf(x) + 1]?.reached.includes(o.status));
              const current = !cancelled && steps.indexOf(s) === idx + 1;
              return (
                <div key={s.key} className={`tl-item ${done ? 'done' : ''} ${current ? 'current' : ''}`}>
                  <div className="tl-dot">{done ? <Check size={17} /> : s.icon}</div>
                  <div>
                    <h4>
                      {s.label} {done && at(s.key === 'PAID' ? 'PAID' : s.key) && <span className="muted small">· {time(at(s.key === 'PAID' ? 'PAID' : s.key))}</span>}
                    </h4>
                    <p className="muted small">{done || current ? s.desc : t.tracking.waiting}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card card-pad stack">
            <h3>{t.tracking.details}</h3>
            {o.items.map((i, k) => (
              <div key={k} className="row-between small">
                <span>
                  {i.quantity} × {demo(i.name)}
                  {i.options.length > 0 && <span className="muted"> ({i.options.map((x) => demo(x.optionName)).join(', ')})</span>}
                </span>
                <span>{money(i.lineTotalCents)}</span>
              </div>
            ))}
            <div className="divider" />
            <div className="row-between strong">
              <span>{t.tracking.totalPaid}</span>
              <span>{money(o.totalCents)}</span>
            </div>
            {delivery && o.address && <p className="small muted">{t.tracking.deliveryTo(addressLine(o.address))}</p>}
            <div className="row wrap">
              <a className="btn btn-sm" href={`tel:${o.restaurant.phone}`}>
                <Phone size={15} /> {t.tracking.callRestaurant}
              </a>
              <Link className="btn btn-sm" to={`/r/${o.restaurant.slug}/menu`}>
                {t.tracking.newOrder}
              </Link>
            </div>
          </div>
        </div>

        {delivery ? (
          <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
            <MapView center={o.restaurant.location} markers={markers} fitKey={`${o.status}-${!!o.driverLocation}`} route={o.driverLocation && o.address?.lat != null && !o.terminal ? [o.driverLocation, { lat: o.address.lat!, lng: o.address.lng! }] : undefined} testId="tracking-map" />
            {o.status === 'DELIVERED' && (
              <div className="alert alert-success" style={{ position: 'absolute', left: 12, right: 12, bottom: 12 }} data-testid="tracking-ended">
                {t.tracking.ended}
              </div>
            )}
            {o.driverLocation && !o.terminal && (
              <span className="pill" style={{ position: 'absolute', top: 12, left: 12, background: '#fff' }} data-testid="driver-position" data-lat={o.driverLocation.lat} data-lng={o.driverLocation.lng}>
                <Clock size={14} /> {t.tracking.positionUpdated(time(o.driverLocation.capturedAt))}
              </span>
            )}
          </div>
        ) : (
          <div className="card card-pad stack center" style={{ alignContent: 'center' }}>
            <Package size={48} color="var(--red)" style={{ margin: '0 auto' }} />
            <h3>{t.checkout.pickupAtRestaurant}</h3>
            <p className="muted">{t.tracking.showNumber(o.number)}</p>
          </div>
        )}
      </div>
    </main>
  );
}
